export interface RestorableWorkspaceResource {
    deletedAt?: Date | string | null;
    id: string;
    [key: string]: unknown;
}

interface OptimisticRestoreStore {
    getState: () => Record<string, unknown>;
    setState: (partial: Record<string, unknown>) => void;
}

export interface OptimisticRestoreStoreConfig {
    collectionKey: string;
    restoreSelection?: boolean;
    selectedKey: string;
    store: OptimisticRestoreStore;
}

export interface OptimisticRestoreTransaction {
    /** Re-applies the restored snapshot to the latest collection state. */
    commit: () => void;
    /** Removes only this optimistic resource and restores the prior selection. */
    rollback: () => void;
}

export interface OptimisticTrashTransaction {
    /** Re-applies the removal against the latest collection state. */
    commit: () => void;
    /** Restores only this resource while retaining concurrent collection changes. */
    rollback: () => void;
    resource: RestorableWorkspaceResource;
}

const getCollection = (state: Record<string, unknown>, collectionKey: string): RestorableWorkspaceResource[] => {
    const collection = state[collectionKey];

    return Array.isArray(collection) ? (collection as RestorableWorkspaceResource[]) : [];
};

/**
 * Creates an idempotent optimistic restore transaction for a Zustand resource
 * collection. `commit` deliberately reads the latest state every time so a
 * list request or realtime packet that completes during the restore cannot
 * permanently overwrite the restored row.
 */
export const createOptimisticCollectionRestoreTransaction = (
    config: OptimisticRestoreStoreConfig,
    resource: RestorableWorkspaceResource,
): OptimisticRestoreTransaction => {
    const initialState = config.store.getState();
    const previousSelected = initialState[config.selectedKey];

    const commit = () => {
        const state = config.store.getState();
        const currentCollection = getCollection(state, config.collectionKey);
        const currentResource = currentCollection.find((item) => item.id === resource.id);
        const optimisticResource = {
            ...resource,
            ...currentResource,
            deletedAt: null,
        };

        config.store.setState({
            [config.collectionKey]: [
                optimisticResource,
                ...currentCollection.filter((item) => item.id !== resource.id),
            ],
            ...(config.restoreSelection ? { [config.selectedKey]: optimisticResource } : {}),
        });
    };

    const rollback = () => {
        const state = config.store.getState();
        const currentCollection = getCollection(state, config.collectionKey);
        const selected = state[config.selectedKey] as { id?: unknown } | null | undefined;

        config.store.setState({
            [config.collectionKey]: currentCollection.filter((item) => item.id !== resource.id),
            ...(config.restoreSelection && selected?.id === resource.id
                ? { [config.selectedKey]: previousSelected }
                : {}),
        });
    };

    commit();

    return { commit, rollback };
};

/**
 * Removes one resource without snapshotting or replacing the entire collection.
 * This keeps concurrent single and bulk Trash mutations isolated from each other.
 */
export const createOptimisticCollectionTrashTransaction = (
    config: OptimisticRestoreStoreConfig,
    resourceId: string,
): OptimisticTrashTransaction => {
    const initialState = config.store.getState();
    const initialCollection = getCollection(initialState, config.collectionKey);
    const previousSelected = initialState[config.selectedKey] as RestorableWorkspaceResource | null | undefined;
    const initialIndex = initialCollection.findIndex((item) => item.id === resourceId);
    const resource =
        initialCollection[initialIndex] ?? (previousSelected?.id === resourceId ? previousSelected : undefined);

    if (!resource) {
        throw new Error(`Workspace resource ${resourceId} is not available in the active collection.`);
    }

    const commit = () => {
        const state = config.store.getState();
        const currentCollection = getCollection(state, config.collectionKey);
        const selected = state[config.selectedKey] as { id?: unknown } | null | undefined;

        config.store.setState({
            [config.collectionKey]: currentCollection.filter((item) => item.id !== resourceId),
            ...(selected?.id === resourceId ? { [config.selectedKey]: null } : {}),
        });
    };

    const rollback = () => {
        const state = config.store.getState();
        const currentCollection = getCollection(state, config.collectionKey);
        const withoutResource = currentCollection.filter((item) => item.id !== resourceId);
        const insertionIndex = Math.min(Math.max(initialIndex, 0), withoutResource.length);
        const nextCollection = [...withoutResource];

        nextCollection.splice(insertionIndex, 0, resource);
        config.store.setState({
            [config.collectionKey]: nextCollection,
            ...(previousSelected?.id === resourceId ? { [config.selectedKey]: previousSelected } : {}),
        });
    };

    commit();

    return { commit, resource, rollback };
};
